import React from 'react';
import { useNavigate } from 'react-router-dom';
import AWSAccountRequest from '../../../models/entities/AWSAccountRequest';
import { Button, TextPassage } from '../../common';
import { Tooltip } from '../../common/components/Tooltip';
import { Badge, TableRow } from '@emorg-prd/standard-react';
import moment from 'moment';
import ActionRow from '../../common/tables/ActionRow';
import WorkflowStateBadge from '../../common/WorkflowStateBadge';
import AWSAccountHomeActionsRow from './AWSAccountHomeActionsRow';
import '../../Subscriptions/SubscriptionsHome/SubscriptionHomeTableRow.css';

interface AWSAccountsHomeTableRowProps {
	account: AWSAccountRequest;
	accountRowRef: React.RefObject<HTMLBaseElement>;
}

const connectedIcon = (
	<path
		fill="currentColor"
		d="M 24.875 0 A 1.0001 1.0001 0 0 0 24.28125 0.28125 L 19.625 4.9375 C 17.42105 3.3463983 14.363638 3.6317919 12.3125 5.59375 C 12.3125 5.6701204 11.457295 6.5035041 10.6875 7.25 L 9.71875 6.28125 A 1.0001 1.0001 0 0 0 8.78125 6 A 1.0001 1.0001 0 0 0 8.28125 7.71875 L 18.28125 17.71875 A 1.016466 1.016466 0 1 0 19.71875 16.28125 L 18.75 15.3125 L 20.40625 13.6875 C 22.366628 11.727122 22.564583 8.6795387 21.03125 6.40625 L 25.71875 1.71875 A 1.0001 1.0001 0 0 0 24.875 0 z M 6.90625 7.96875 A 1.0001 1.0001 0 0 0 6.78125 8 A 1.0001 1.0001 0 0 0 6.28125 9.71875 L 7.25 10.6875 L 5.59375 12.3125 C 3.633372 14.272878 3.4354172 17.320461 4.96875 19.59375 L 0.28125 24.28125 A 1.016466 1.016466 0 1 0 1.71875 25.71875 L 6.375 21.0625 C 8.5789499 22.653602 11.636362 22.368208 13.6875 20.40625 C 13.6875 20.32988 14.542705 19.496496 15.3125 18.75 L 16.28125 19.71875 A 1.016466 1.016466 0 1 0 17.71875 18.28125 L 7.71875 8.28125 A 1.0001 1.0001 0 0 0 6.90625 7.96875 z"
	/>
);
const disconnectedIcon = (
	<path
		fill="currentColor"
		d="M 24.875 0 A 1.0001 1.0001 0 0 0 24.28125 0.28125 L 22.625 1.9375 C 20.42105 0.3463983 17.363638 0.63179191 15.3125 2.59375 C 15.3125 2.6701204 14.457295 3.5035041 13.6875 4.25 L 12.71875 3.28125 A 1.0001 1.0001 0 0 0 11.90625 2.96875 A 1.0001 1.0001 0 0 0 11.78125 3 A 1.0001 1.0001 0 0 0 11.28125 4.71875 L 13.5625 7 L 10.28125 10.28125 A 1.016466 1.016466 0 1 0 11.71875 11.71875 L 15 8.4375 L 17.5625 11 L 14.28125 14.28125 A 1.016466 1.016466 0 1 0 15.71875 15.71875 L 19 12.4375 L 21.28125 14.71875 A 1.016466 1.016466 0 1 0 22.71875 13.28125 L 21.75 12.3125 L 23.40625 10.6875 C 25.366628 8.727122 25.564583 5.6795387 24.03125 3.40625 L 25.71875 1.71875 A 1.0001 1.0001 0 0 0 24.875 0 z M 3.90625 10.96875 A 1.0001 1.0001 0 0 0 3.78125 11 A 1.0001 1.0001 0 0 0 3.28125 12.71875 L 4.25 13.6875 L 2.59375 15.3125 C 0.63337202 17.272878 0.43541722 20.320461 1.96875 22.59375 L 0.28125 24.28125 A 1.016466 1.016466 0 1 0 1.71875 25.71875 L 3.375 24.0625 C 5.5789499 25.653602 8.636362 25.368208 10.6875 23.40625 C 10.6875 23.32988 11.542705 22.496496 12.3125 21.75 L 13.28125 22.71875 A 1.016466 1.016466 0 1 0 14.71875 21.28125 L 4.71875 11.28125 A 1.0001 1.0001 0 0 0 3.90625 10.96875 z"
	/>
);

const AWSAccountsHomeTableRow: React.FC<AWSAccountsHomeTableRowProps> = ({ account, accountRowRef }) => {
	const navigate = useNavigate();

	const handleViewDetails = () => {
		navigate(`/awsAccounts/${account.id}`);
	};

	return (
		<TableRow>
			<ActionRow
				key={account.id}
				actionRef={accountRowRef}
				actionCell={
					<Tooltip optionalClass="em-u-margin-half" content={String(account.id)}>
						<div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
							<span data-tip={account.accountName} className="table__cell-name">
								{account.accountName ?? 'N/A'}
							</span>
							{account.isRecentlyImportedAccount() && (
								<Badge
									type="em-c-badge--caution"
									text="Imported"
								/>
							)}
						</div>
					</Tooltip>
				}
				actions={
					<>
						<AWSAccountHomeActionsRow awsAccount={account} />
					</>
				}
			/>
			<React.Fragment key={account.id}>
				<svg
					data-tip={`${account.isManaged ? 'Managed' : 'Disconnected'} Accounts`}
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 26 26"
					enableBackground="new 0 0 26 26"
					className=" em-c-icon em-c-icon--small em-c-icon_svg "
				>
					{account.isManaged ? connectedIcon : disconnectedIcon}
				</svg>
			</React.Fragment>
			<React.Fragment key={account.id}>
				<WorkflowStateBadge
					workflowState={account.workflowInstance.currentWorkflowInstanceState.workflowState}
					fullWidth
				/>
			</React.Fragment>
			<React.Fragment key={account.id}>
				<TextPassage>
					{account.organization?.L4 && account.organization?.L3 ? (
						<div style={{ display: 'flex', flexDirection: 'column' }}>
							<p className="custom-org-margin table__cell-org">
								{account.organization.L3.shortName ?? account.organization.L3.displayName}
							</p>
							<p data-tip={account.organization.L3.displayName} className="custom-org-margin table__cell-org">
								{account.organization.L4.displayName}
							</p>
						</div>
					) : (
						<p>No organization</p>
					)}
				</TextPassage>
			</React.Fragment>
			<React.Fragment key={account.id}>
				<span data-tip={account.owner?.displayName} className="table__cell">
					{account.owner?.displayName ?? '-'}
				</span>
			</React.Fragment>
			<React.Fragment key={account.id}>
				<span data-tip={account.custodian?.displayName} className="table__cell">
					{account.custodian?.displayName ?? '-'}
				</span>
			</React.Fragment>
			<React.Fragment key={account.id}>
				{moment(account.workflowInstance.createdDate).format('MM/DD/YYYY')}
			</React.Fragment>
			<React.Fragment key={account.id}>
				<Button style={{ width: 120, height: 34 }} onClick={handleViewDetails} variant="primary">
					See Details
				</Button>
			</React.Fragment>
		</TableRow>
	);
};

export default AWSAccountsHomeTableRow;
